import {
  DropdownSelect,
  FlexBox,
  Icon,
  SelectItem,
  Tabs,
} from '@dixa/lib-shared-habla';
import { CollectionManagementNav } from 'collection/pages/collectionEdit/components/layout/layout';
import { ItemType } from 'lib-knowledge-utils/components/itemType';
import Page from 'lib-knowledge-utils/components/page/index';
import {
  aiSuggestionsSettingsPath,
  routeGenerator,
} from 'lib-knowledge-utils/routes';
import { useState } from 'react';
import { matchPath, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { DefaultLanguageForm } from 'settings/components/defaultLanguageForm';
import { EnabledLanguagesForm } from 'settings/components/enabledLanguagesForm';
import { z } from 'zod';

import { useShowArticleSuggestions } from 'hooks/useShowArticleSuggestions';

import styles from './settingsPage.module.scss';
import { useHandleLanguageSelection } from './useHandleLanguageSelection';

const listItemSchema = z.object({
  name: z.string(),
  id: z.string(),
});
export const knowledgeSettingsFormSchema = z.object({
  defaultLanguage: z.array(listItemSchema),
  enabledLanguageIds: z.array(z.string()),
});

const tabs = [
  { id: 'general', label: 'General' },
  { id: 'ai-suggestions', label: 'AI suggestions' },
];

/** The settings shell: the head, and a tab per area once there is more than one. */
export default function KnowledgeSettingsPage(): JSX.Element {
  const showArticleSuggestions = useShowArticleSuggestions();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  // The versions pages belong to the AI suggestions tab, not one of their own.
  const selectedTab = matchPath(
    { path: aiSuggestionsSettingsPath.pattern, end: false },
    pathname,
  )
    ? 'ai-suggestions'
    : 'general';

  return (
    <Page>
      <Page.Body>
        <CollectionManagementNav />
        <FlexBox
          flexDirection="column"
          width="100%"
          className={styles.pageContainer}
        >
          <Page.Head slotLeft={<Page.HeadTitle title="Knowledge settings" />} />
          {showArticleSuggestions && (
            <Page.Tabs>
              <Tabs
                tabs={tabs}
                selectedId={selectedTab}
                onChange={(id) =>
                  navigate(
                    id === 'ai-suggestions'
                      ? routeGenerator.aiSuggestionsSettings()
                      : routeGenerator.knowledgeSettingsPath(),
                  )
                }
              />
            </Page.Tabs>
          )}
          <FlexBox flexDirection="column">
            <Outlet />
          </FlexBox>
        </FlexBox>
      </Page.Body>
    </Page>
  );
}

/** Languages: the default one, and which are enabled. */
export function GeneralSettings(): JSX.Element {
  const { handleLanguageSelection, inactiveLanguages } =
    useHandleLanguageSelection();

  const [searchTerm, setSearchTerm] = useState<string>('');

  return (
    <>
      <Page.Section title="Settings">
        <DefaultLanguageForm />
      </Page.Section>
      <Page.Section
        title="Enabled languages"
        controls={
          <DropdownSelect
            fixed
            noArrow
            hasSearch
            onSearch={(value) =>
              setSearchTerm(value !== undefined ? value : '')
            }
            onSelect={handleLanguageSelection}
            items={[
              ...inactiveLanguages
                .filter((lang) =>
                  lang.description
                    .toLowerCase()
                    .includes(searchTerm.toLowerCase()),
                )
                .map((lang) => {
                  const item: ItemType = {
                    id: lang.languageId,
                    name: lang.description,
                  };
                  return item;
                }),
            ]}
            selectedItems={[]}
            itemComponent={SelectItem}
            dropdownProps={{
              style: {
                minWidth: '280px',
              },
            }}
          >
            <FlexBox gap="200">
              <Icon name="plus" />
              Enable language
            </FlexBox>
          </DropdownSelect>
        }
      >
        <EnabledLanguagesForm />
      </Page.Section>
    </>
  );
}
